import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { ChipRow, EmptyState, Pill, Spinner } from "../components/ui";
import AppHeader from "../components/AppHeader";
import { Plus, Video, Phone, Users } from "lucide-react";
import { formatDate, isUpcoming } from "../lib/dates";

const TEAMS = [
  { key: "all", label: "All teams" }, { key: "sampling", label: "Sampling" },
  { key: "production", label: "Production" }, { key: "general", label: "General" },
];
const STATUSES = [
  { key: "all", label: "All" }, { key: "scheduled", label: "Scheduled" },
  { key: "completed", label: "Completed" }, { key: "cancelled", label: "Cancelled" },
];
const DATE_FILTERS = [
  { key: "upcoming", label: "Upcoming" },
  { key: "past", label: "Past" },
  { key: "all", label: "All dates" },
];

export default function Meetings() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [team, setTeam] = useState("all");
  const [status, setStatus] = useState("all");
  const [dateFilter, setDateFilter] = useState("upcoming");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const sp = new URLSearchParams();
    if (team !== "all") sp.set("team", team);
    if (status !== "all") sp.set("status", status);
    try { setItems(await api.get(`/meetings?${sp}`)); } finally { setLoading(false); }
  }, [team, status]);

  useEffect(() => { load(); }, [load]);

  const icon = (t) => t === "google_meet" ? <Video size={12} /> : t === "over_call" ? <Phone size={12} /> : <Users size={12} />;

  const filtered = items.filter((m) => {
    if (dateFilter === "all") return true;
    const upcoming = isUpcoming(m.date);
    return dateFilter === "upcoming" ? upcoming : !upcoming;
  });

  return (
    <>
      <AppHeader title="Meetings" />
      <div className="sg-main sg-fade-in">
        <div style={{ marginBottom: 8 }}><ChipRow items={DATE_FILTERS} value={dateFilter} onChange={setDateFilter} testIdPrefix="meetings-date" /></div>
        <div style={{ marginBottom: 8 }}><ChipRow items={TEAMS} value={team} onChange={setTeam} testIdPrefix="meetings-team" /></div>
        <div style={{ marginBottom: 14 }}><ChipRow items={STATUSES} value={status} onChange={setStatus} testIdPrefix="meetings-status" /></div>

        {loading ? <Spinner /> : filtered.length === 0 ? (
          <EmptyState
            title={dateFilter === "upcoming" ? "No upcoming meetings" : "No meetings"}
            hint={dateFilter === "upcoming" ? "Tap + to book one, or switch to Past / All dates." : "Tap + to book a new meeting."}
          />
        ) : (
          filtered.map((m) => (
            <div key={m.meeting_id} className="sg-list-card" onClick={() => navigate(`/meetings/${m.meeting_id}`)} data-testid={`meeting-row-${m.meeting_id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.company_name || m.customer_name}</div>
                  <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>{formatDate(m.date)} · {m.time}</div>
                </div>
                <Pill label={m.status} tone={m.status === "completed" ? "success" : m.status === "cancelled" ? "danger" : "brand"} />
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                <span style={tagStyle}>{icon(m.meeting_type)} {String(m.meeting_type).replace("_", " ")}</span>
                <span style={tagStyle}>{m.team}</span>
              </div>
            </div>
          ))
        )}
      </div>

      <button className="sg-fab" onClick={() => navigate("/meetings/new")} data-testid="meetings-add-button" aria-label="Book meeting">
        <Plus size={24} />
      </button>
    </>
  );
}

const tagStyle = { display: "inline-flex", alignItems: "center", gap: 4, background: "var(--surface-subtle)", color: "var(--muted)", padding: "3px 8px", borderRadius: 6, fontSize: 11, textTransform: "capitalize" };
